import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Sparkles, Bell, Megaphone, ArrowRight, Loader2, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inscription() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    confirm: '',
    notify_launch: true,
    notify_updates: true,
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const inputCls =
    'w-full px-4 py-3 rounded-md bg-neutral-900/80 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 transition-colors';

  // Password strength
  const pwdScore = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0..5
  })();
  const pwdLabel = ['Trop court', 'Faible', 'Moyen', 'Bon', 'Fort', 'Excellent'][pwdScore];
  const pwdColor = ['bg-neutral-700', 'bg-red-500', 'bg-orange-500', 'bg-yellow-400', 'bg-lime-500', 'bg-green-500'][pwdScore];

  const onSubmit = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Email invalide', description: 'Merci de saisir une adresse email valide.' });
      return;
    }
    if (form.password.length < 8) {
      toast({ title: 'Mot de passe trop court', description: 'Au moins 8 caractères requis.' });
      return;
    }
    if (form.password !== form.confirm) {
      toast({ title: 'Les mots de passe ne correspondent pas', description: 'Vérifiez la confirmation.' });
      return;
    }
    if (!form.consent) {
      toast({ title: 'Consentement requis', description: 'Merci d\'accepter le traitement de vos données.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, {
        email,
        name: form.name.trim() || null,
        password: form.password,
        notify_launch: form.notify_launch,
        notify_updates: form.notify_updates,
        consent: form.consent,
      });
      toast({ title: 'Code envoyé !', description: 'Vérifiez votre email pour récupérer le code à 6 chiffres.' });
      navigate(`/verification?email=${encodeURIComponent(email)}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Une erreur est survenue. Réessayez plus tard.';
      toast({ title: 'Erreur', description: typeof msg === 'string' ? msg : 'Erreur de validation.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.18),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <Bell size={14} /> Accès anticipé
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-yellow-400 leading-[1.05]">Rejoignez la ruche</h1>
          <p className="mt-5 text-neutral-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Créez votre compte BuzzBump dès maintenant. Vous recevrez un code par email pour valider votre inscription.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
        <form onSubmit={onSubmit} className="p-7 md:p-10 rounded-2xl border border-white/10 bg-neutral-900/40">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Inscription</h2>
          <p className="text-neutral-400 mb-7">Votre mot de passe vous permettra de vous connecter à l'app dès sa sortie.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-neutral-300 mb-2">Email <span className="text-yellow-400">*</span></label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} placeholder="vous@exemple.com"
                maxLength={120} autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm text-neutral-300 mb-2">Prénom <span className="text-neutral-500">(optionnel)</span></label>
              <input
                type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls} placeholder="Comment vous appeler ?"
                maxLength={80} autoComplete="given-name"
              />
            </div>

            <div>
              <label className="block text-sm text-neutral-300 mb-2">Mot de passe <span className="text-yellow-400">*</span></label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  required minLength={8} maxLength={200}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`${inputCls} pr-12`} placeholder="Au moins 8 caractères"
                  autoComplete="new-password"
                />
                <button
                  type="button" onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-yellow-400"
                  aria-label="Afficher / masquer le mot de passe"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${pwdColor}`} style={{ width: `${(pwdScore / 5) * 100}%` }} />
                  </div>
                  <span className="text-xs text-neutral-400 w-20 text-right">{pwdLabel}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-300 mb-2">Confirmer le mot de passe <span className="text-yellow-400">*</span></label>
              <input
                type={showPwd ? 'text' : 'password'}
                required minLength={8} maxLength={200}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className={inputCls} placeholder="Saisissez à nouveau"
                autoComplete="new-password"
              />
              {form.confirm && form.password !== form.confirm && (
                <p className="mt-1 text-xs text-red-400">Les mots de passe ne correspondent pas</p>
              )}
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-white/10 hover:border-yellow-400/30 bg-neutral-950/40 transition-colors">
              <input type="checkbox" checked={form.notify_launch}
                onChange={(e) => setForm({ ...form, notify_launch: e.target.checked })}
                className="mt-1 h-4 w-4 accent-yellow-400 flex-shrink-0" />
              <span>
                <span className="flex items-center gap-2 text-white font-semibold"><Megaphone size={16} className="text-yellow-400" /> Sortie officielle</span>
                <span className="block mt-1 text-sm text-neutral-400">Soyez prévenu dès que l'app est disponible.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-white/10 hover:border-yellow-400/30 bg-neutral-950/40 transition-colors">
              <input type="checkbox" checked={form.notify_updates}
                onChange={(e) => setForm({ ...form, notify_updates: e.target.checked })}
                className="mt-1 h-4 w-4 accent-yellow-400 flex-shrink-0" />
              <span>
                <span className="flex items-center gap-2 text-white font-semibold"><Sparkles size={16} className="text-yellow-400" /> Avancement du projet</span>
                <span className="block mt-1 text-sm text-neutral-400">Suivez les coulisses et les nouvelles fonctionnalités.</span>
              </span>
            </label>
          </div>

          <label className="mt-6 flex items-start gap-3 text-xs text-neutral-400 leading-relaxed">
            <input type="checkbox" checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 h-4 w-4 accent-yellow-400 flex-shrink-0" required />
            <span>
              Je consens à ce que mes données (email + mot de passe hashé) soient conservées par BuzzBump. Je peux me désinscrire en contactant <a href="mailto:contact.buzzbump@gmail.com" className="text-yellow-400 hover:underline">contact.buzzbump@gmail.com</a>. <span className="text-yellow-400">*</span>
            </span>
          </label>

          <button type="submit" disabled={loading}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Création du compte...</> : <><ShieldCheck size={18} /> Créer mon compte</>}
          </button>

          <p className="mt-4 text-xs text-neutral-500 text-center">
            Déjà inscrit mais pas encore vérifié ? <Link to="/verification" className="text-yellow-400 hover:underline">Saisir mon code</Link>
          </p>
        </form>
      </section>
    </div>
  );
}
